import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get, isEmpty } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EmailFeedbackHeader, EmailFeedbackLoading } from 'src/containers';
import { formatNoteText } from 'src/pagesDashboard/NewUserReport/utils';
import {
  QUERY_KEYS as FEEDBACK_QUERY_KEYS,
  giveEmailFeedbackQuery,
  useEmailFeedback
} from 'src/queries/feedback';
import {
  Base,
  Button,
  Radio,
  ReviewAiSuggestions,
  toast
} from 'src/components/';
import appUtils from 'src/components/appUtils';
import FeedbackQuill from 'src/components/Quill/FeedbackQuill';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonQuestionUtils from 'common/commonQuestionsUtils';
import commonReviewUtils from 'common/commonReviewUtils';

const { renderShareCommentsVisibilityOptions } = COMMON_CONSTANTS;

const EmailFeedback = () => {
  const urlParams = useParams();
  const { id: feedbackId, companyId } = urlParams;
  const navigate = useNavigate();
  const { enableAiSuggestionsForReviewAndFeedback } = useFlags();
  const queryClient = useQueryClient();

  const {
    data: feedback,
    error: errorFeedback,
    isFetching: isFetchingFeedback,
    isError: isErrorFeedback
  } = useEmailFeedback({ id: feedbackId, companyId });

  const { mutateAsync: giveEmailFeedback, isLoading: giveFeedbackLoading } = giveEmailFeedbackQuery();

  const reviewVisibilityDefaultOption = get(
    feedback,
    'company.settings.reviewVisibilityDefaultOption',
    null
  );
  const reviewVisibilitySettings = get(
    feedback,
    'company.settings.reviewVisibilityOptions',
    {}
  );

  const {
    watch,
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    defaultValues: {
      feedbackText: '',
      commentVisible: reviewVisibilityDefaultOption
    }
  });

  const [resetSuggestions, setResetSuggestions] = useState(false);

  const formFeedbackText = watch('feedbackText');

  const isLoading = isFetchingFeedback || giveFeedbackLoading;
  const isReady = !isLoading && !isEmpty(feedback);

  if (isErrorFeedback) {
    const ERROR_STYLE = 'right-standard m-3 sm:w-38rem md:w-45rem sm:mx-auto mb-12 p-4 px-8 sm:px-1 rounded-md bg-cloud-blue mt-0';

    const errorMessage = get(
      errorFeedback,
      'response.data.message',
      'Feedback was not found or has already been reviewed. Be on the lookout for the next one!'
    );
    return (
      <div className='pt-10 h-20 w-full'>
        <div className={ERROR_STYLE}>
          <Base variant='transparent' classes='text-center pb-6'>
            <p className='font-bold mt-4 mb-0 text-xl'>{errorMessage}</p>
            <Button
              onClick={() => navigate(appUtils.getHomeRoute())}
              variant='black'
              classes='w-[170px] mt-6'
            >
              Back to Home
            </Button>
          </Base>
        </div>
      </div>
    );
  }

  if (!isReady) {
    return <EmailFeedbackLoading />;
  }

  const {
    revieweeId,
    reviewerId,
    revieweeImage,
    revieweeName,
    revieweeTitle,
    requestText
  } = feedback;

  const visibilityOptions = renderShareCommentsVisibilityOptions({
    revieweeName
  });

  const shareCommentOptions = commonQuestionUtils.getShareCommentOptions(
    reviewVisibilitySettings,
    visibilityOptions
  );

  const saveFeedback = async (formValues) => {
    try {
      const { feedbackText, commentVisible } = formValues;
      let visible = commentVisible;

      if (!commentVisible) visible = reviewVisibilityDefaultOption;

      const result = await giveEmailFeedback({
        companyId,
        reviewerId,
        revieweeId,
        text: commonReviewUtils.commentToHTML(feedbackText),
        visible,
        feedbackId
      });

      if (result.success) {
        queryClient.removeQueries(FEEDBACK_QUERY_KEYS.EMAIL_FEEDBACK);
        queryClient.invalidateQueries(FEEDBACK_QUERY_KEYS.FEEDBACK);
        queryClient.invalidateQueries(FEEDBACK_QUERY_KEYS.FEEDBACK_FEED);
        queryClient.invalidateQueries(FEEDBACK_QUERY_KEYS.FEEDBACK_COUNT);
      }

      if (!result || !result.success) {
        const message = result.message
          ?? 'Uh oh, we ran into an issue. Please try again later!';
        toast.error(message);
        return false;
      }
      appUtils.scrollToTop();
      toast.show('Feedback submitted. Thank you!');
      return navigate(appUtils.getHomeRoute());
    } catch (error) {
      toast.error('Uh oh, we ran into an issue. Please try again!');
      return false;
    }
  };

  return (
    <form
      className='flex flex-col items-center w-full'
      onSubmit={handleSubmit(saveFeedback)}
    >
      <div className='max-w-1240 w-full'>
        <EmailFeedbackHeader
          revieweeImage={revieweeImage}
          revieweeName={revieweeName}
          revieweeTitle={revieweeTitle}
        />
        <div className='mx-auto w-5/6'>
          <div className='mb-3 flex flex-col md:flex-row'>
            <div className='text-gray-500 mb-8 md:w-2/5'>
              <div className='uppercase font-bold text-xl mb-1'>Request</div>
            </div>
            <div className='text-lg md:w-3/5 whitespace-pre-line'>
              {requestText}
            </div>
          </div>
          <div className='mb-3 flex flex-col md:flex-row'>
            <div className='text-gray-500 mb-2 md:w-2/5'>
              <div className='uppercase font-bold text-xl mb-1'>Feedback</div>
              <div className='text-base pr-6'>
                Give an example of what you think
                {' '}
                <br />
                (max: 2000 characters)
              </div>
            </div>
            <div className='flex flex-col md:w-3/5 '>
              <Controller
                name='feedbackText'
                control={control}
                rules={{
                  required: {
                    value: true,
                    message:
                      'Please provide a longer comment to answer this question.'
                  },
                  validate: (value) => {
                    // min text length === 10
                    if (!value) return false;
                    const plainText = commonReviewUtils.getPlainTextFromHTML(value);
                    const plainTextWithNoSpaces = plainText.replaceAll(' ', '');
                    if (plainTextWithNoSpaces.length < 10) return false;
                    return true;
                  }
                }}
                render={({ field }) => (
                  <FeedbackQuill
                    className='direct-feedback-quill'
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(formatNoteText(value));
                    }}
                  />
                )}
              />
              <div className='text-red whitespace-nowrap flex items-center'>
                {errors?.feedbackText?.message}
              </div>
            </div>
          </div>

          {enableAiSuggestionsForReviewAndFeedback ? (
            <ReviewAiSuggestions
              comments={formFeedbackText}
              userId={revieweeId}
              revieweeName={revieweeName}
              companyid={companyId}
              reviewerId={reviewerId}
              resetSuggestions={resetSuggestions}
              setResetSuggestions={setResetSuggestions}
            />
          ) : null}

          <div className='flex flex-col mb-4 md:flex-row'>
            <div className='uppercase text-gray-500 font-bold text-xl mb-2 md:w-2/5'>
              Comment visibility
            </div>
            <div className='md:w-3/5'>
              {shareCommentOptions.length > 1
                ? shareCommentOptions.map((option) => (
                  <div className='font-semibold' key={option.title}>
                    <Controller
                      name='commentVisible'
                      control={control}
                      rules={{ required: false }}
                      render={({ field: { value, onChange } }) => (
                        <Radio
                          name='commentVisible'
                          value={value}
                          title={option.title}
                          checked={
                              !value
                                ? option.value === reviewVisibilityDefaultOption
                                : value === option.value
                            }
                          onChange={() => onChange(option.value)}
                        />
                      )}
                    />
                    <div>
                      <p className='text-dark-grey'>{option.comments}</p>
                    </div>
                  </div>
                ))
                : null}
              {shareCommentOptions.length === 1 ? (
                <>
                  <p className='fontSize16 mb-0 pd-0 bold'>
                    {shareCommentOptions[0].title}
                  </p>

                  <div className='ml-0'>
                    <p className='mb-0 text-dark-grey'>
                      {shareCommentOptions[0].comments}
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          <div className='flex w-full justify-end pb-8'>
            <Button
              data-testid='submit-feedback-btn'
              classes='w-32'
              disabled={!isValid || isSubmitting}
              variant='yellow'
              type='submit'
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EmailFeedback;
